import {
  GeneralError,
  LoginWithEmailUseCase,
  LogoutUseCase,
  SignInWithGoogleUseCase
} from '51trips-frontend-core'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { onAuthStateChanged } from 'firebase/auth'

import { EmailPasswordForm } from '../../components/auth/EmailPasswordForm'
import { LoginMethodSelector } from '../../components/auth/LoginMethodSelector'
import { MagicLinkConfirmEmailForm } from '../../components/auth/MagicLinkConfirmEmailForm'
import { MagicLinkForm } from '../../components/auth/MagicLinkForm'
import { PhoneLoginForm } from '../../components/auth/PhoneLoginForm'
import { ConfirmPhoneVerificationCodeUseCase } from '../../domain/useCases/auth/ConfirmPhoneVerificationCodeUseCase'
import { SendMagicLinkUseCase } from '../../domain/useCases/auth/SendMagicLinkUseCase'
import { SendPhoneVerificationCodeUseCase } from '../../domain/useCases/auth/SendPhoneVerificationCodeUseCase'
import { SignInWithMagicLinkUseCase } from '../../domain/useCases/auth/SignInWithMagicLinkUseCase'
import { WhoAmIUseCase } from '../../domain/useCases/auth/WhoAmIUseCase.ts'
import { auth } from '../../firebase.ts'
import { setUser } from '../../features/auth.ts'
import { AnalyticsRepository } from '../../repositories/AnalyticsRepository'
import { AuthenticationRepository } from '../../repositories/AuthenticationRepository'
import { PartnerRepository } from '../../repositories/PartnerRepository.ts'
import { UserRepository } from '../../repositories/UserRepository'
import { useDispatch } from 'react-redux'
import { consumeReturnTo, rememberReturnTo, safeReturnTo } from '../../utils/loginReturnTo.ts'

const authenticationRepository = new AuthenticationRepository()

const loginWithEmailUseCase = new LoginWithEmailUseCase(authenticationRepository)
const signInWithGoogleUseCase = new SignInWithGoogleUseCase(
  authenticationRepository,
  new UserRepository(),
  new AnalyticsRepository()
)
const logoutUseCase = new LogoutUseCase(authenticationRepository)
const sendMagicLinkUseCase = new SendMagicLinkUseCase(authenticationRepository)
const signInWithMagicLinkUseCase = new SignInWithMagicLinkUseCase(authenticationRepository)
const sendPhoneVerificationCodeUseCase = new SendPhoneVerificationCodeUseCase(authenticationRepository)
const confirmPhoneVerificationCodeUseCase = new ConfirmPhoneVerificationCodeUseCase(
  authenticationRepository
)

const whoAmIUseCase = new WhoAmIUseCase(new PartnerRepository())

type LoginView = 'select' | 'email' | 'magic-sent' | 'phone' | 'magic-link-confirm'

export const LoginPage: React.FC = () => {
  /// i18n
  const { t } = useTranslation()

  /// Navigation
  const navigate = useNavigate()
  const location = useLocation()
  // Set by the dashboard auth guard: the URL to go back to after login.
  const returnTo = safeReturnTo(new URLSearchParams(location.search).get('redirect'))

  /// States
  const [view, setView] = useState<LoginView>('select')
  const [magicLinkEmail, setMagicLinkEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const dispatch = useDispatch()

  useEffect(() => {
    // Remembered too, for the magic-link callback (it lands without ?redirect=).
    rememberReturnTo(returnTo)
    const authError = (location.state as { authError?: string } | null)?.authError
    if (authError === 'noAccess') {
      setErrorMessage(t('accountWithoutAccess', { ns: 'auth' }))
    }
  }, [])

  /// Magic link callback handling
  useEffect(() => {
    if (!signInWithMagicLinkUseCase.isMagicLinkSignIn(window.location.href)) {
      return
    }

    const storedEmail = signInWithMagicLinkUseCase.storedEmailForMagicLinkSignIn
    if (storedEmail) {
      completeMagicLinkSignIn(storedEmail)
    } else {
      setView('magic-link-confirm')
    }
  }, [])

  /// Actions
  const forceLogoutForMissingAccess = () => {
    logoutUseCase.logout().subscribe({
      next: () => setErrorMessage(t('accountWithoutAccess', { ns: 'auth' })),
      error: () => setErrorMessage(t('accountWithoutAccess', { ns: 'auth' }))
    })
  }

  const resolveAuthenticatedUser = () => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult()

        const role = idTokenResult.claims.role
        const agencyId = idTokenResult.claims.agencyId
        const tourOperatorId = idTokenResult.claims.tourOperatorId
        const partnerId = tourOperatorId || agencyId || (role === 'admin' ? user.uid : undefined)

        if (!role || !partnerId) {
          forceLogoutForMissingAccess()
          return
        }

        whoAmIUseCase
          .whoAmI(role as string, partnerId as string)
          .then((partner) => {
            dispatch(
              setUser({
                ...partner,
                email: user.email ?? undefined
              })
            )
            navigate(consumeReturnTo(returnTo) ?? '/dashboard')
          })
          .catch(() => {
            forceLogoutForMissingAccess()
          })
      }
    })
  }

  const completeMagicLinkSignIn = (email: string) => {
    setIsSubmitting(true)
    setErrorMessage(undefined)

    signInWithMagicLinkUseCase.signInWithMagicLink(email, window.location.href).subscribe({
      next: () => {
        setIsSubmitting(false)
        resolveAuthenticatedUser()
      },
      error: (error: GeneralError) => {
        setErrorMessage(error.message || t('magicLink.confirmEmailError', { ns: 'auth' }))
        setIsSubmitting(false)
      }
    })
  }

  const handleGoogleLogin = () => {
    if (isSubmitting) {
      return
    }
    setIsSubmitting(true)
    setErrorMessage(undefined)

    signInWithGoogleUseCase.signInWithGoogle().subscribe({
      next: () => {
        setIsSubmitting(false)
        resolveAuthenticatedUser()
      },
      error: (error: GeneralError) => {
        const errorCode = (error.underlyingError as { code?: string } | undefined)?.code
        if (errorCode === 'auth/user-not-found' || errorCode === 'auth/wrong-password') {
          setErrorMessage(t('userNotFoundWrongPassword', { ns: 'auth' }))
        }
        setIsSubmitting(false)
      }
    })
  }

  /// Render

  switch (view) {
    case 'select':
      return (
        <LoginMethodSelector
          onSelectEmail={() => setView('email')}
          onSelectPhone={() => setView('phone')}
          onSelectGoogle={handleGoogleLogin}
          disabled={isSubmitting}
          errorMessage={errorMessage}
        />
      )
    case 'email':
      return (
        <EmailPasswordForm
          loginWithEmailUseCase={loginWithEmailUseCase}
          sendMagicLinkUseCase={sendMagicLinkUseCase}
          onSuccess={resolveAuthenticatedUser}
          onMagicLinkSent={(email) => {
            setMagicLinkEmail(email)
            setView('magic-sent')
          }}
          onBack={() => setView('select')}
        />
      )
    case 'magic-sent':
      return (
        <MagicLinkForm
          sendMagicLinkUseCase={sendMagicLinkUseCase}
          email={magicLinkEmail}
          onUsePasswordInstead={() => setView('email')}
        />
      )
    case 'phone':
      return (
        <PhoneLoginForm
          sendPhoneVerificationCodeUseCase={sendPhoneVerificationCodeUseCase}
          confirmPhoneVerificationCodeUseCase={confirmPhoneVerificationCodeUseCase}
          onSuccess={resolveAuthenticatedUser}
          onBack={() => setView('select')}
        />
      )
    case 'magic-link-confirm':
      return (
        <MagicLinkConfirmEmailForm
          onConfirm={completeMagicLinkSignIn}
          errorMessage={errorMessage}
          isSubmitting={isSubmitting}
        />
      )
  }
}
